import { useEffect, useState } from "react";

import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import "../styles/category-form.css";

import {
  generateSlug,
  normalizeName
} from "../utils/category.utils";

const CategoryForm = ({
  mode = "create",
  initialData = null,
  onSubmit,
  onCancel,
  loading = false,
  serverError = ""
}) => {
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] =
    useState("");
  const [displayOrder, setDisplayOrder] =
    useState(0);
  const [isActive, setIsActive] =
    useState(true);

  const [errors, setErrors] = useState({});
  const [slugManuallyChanged, setSlugManuallyChanged] =
    useState(false);

  useEffect(() => {
    if (!initialData) {
      return;
    }

    setName(initialData.name || "");
    setSlug(initialData.slug || "");
    setDescription(
      initialData.description || ""
    );

    setDisplayOrder(
      initialData.display_order ?? 0
    );

    setIsActive(
      initialData.is_active !== false
    );

    setSlugManuallyChanged(true);
  }, [initialData]);

  const handleNameChange = (event) => {
    const value = event.target.value;

    setName(value);

    if (!slugManuallyChanged) {
      setSlug(generateSlug(value));
    }

    if (errors.name) {
      setErrors((current) => ({
        ...current,
        name: ""
      }));
    }
  };

  const handleSlugChange = (event) => {
    const value = event.target.value;

    setSlug(generateSlug(value));
    setSlugManuallyChanged(true);

    if (errors.slug) {
      setErrors((current) => ({
        ...current,
        slug: ""
      }));
    }
  };

  const validate = () => {
    const nextErrors = {};

    const normalizedName =
      normalizeName(name);

    if (normalizedName.length < 2) {
      nextErrors.name =
        "Name must be at least 2 characters";
    }

    if (normalizedName.length > 150) {
      nextErrors.name =
        "Name cannot exceed 150 characters";
    }

    if (slug.length > 150) {
      nextErrors.slug =
        "Slug cannot exceed 150 characters";
    }

    if (
      mode === "edit" &&
      !slug.trim()
    ) {
      nextErrors.slug =
        "Slug is required";
    }

    if (description.length > 5000) {
      nextErrors.description =
        "Description cannot exceed 5000 characters";
    }

    const numericDisplayOrder =
      Number(displayOrder);

    if (
      !Number.isInteger(
        numericDisplayOrder
      ) ||
      numericDisplayOrder < 0
    ) {
      nextErrors.displayOrder =
        "Display order must be a non-negative integer";
    }

    setErrors(nextErrors);

    return (
      Object.keys(nextErrors).length === 0
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) {
      return;
    }

    const data = {
      name: normalizeName(name),
      slug: slug.trim(),
      description:
        description.trim() || null,
      displayOrder: Number(displayOrder)
    };

    if (mode === "edit") {
      data.isActive = isActive;
    }

    await onSubmit(data);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="category-form"
    >
      {serverError && (
        <div className="category-form-error">
          {serverError}
        </div>
      )}

      <Input
        label="Category name"
        placeholder="Enter category name"
        value={name}
        onChange={handleNameChange}
        error={errors.name}
        disabled={loading}
      />

      <div className="category-form-field">
        <label
          htmlFor="category-slug"
          className="category-form-label"
        >
          Slug
        </label>

        <input
          id="category-slug"
          type="text"
          value={slug}
          onChange={handleSlugChange}
          placeholder="category-slug"
          disabled={loading}
          className="category-form-input"
        />

        {errors.slug && (
          <p className="category-form-field-error">
            {errors.slug}
          </p>
        )}

        <p className="category-form-help">
          Leave the generated slug unless you want to
          customize it.
        </p>
      </div>

      <div className="category-form-field">
        <label
          htmlFor="category-description"
          className="category-form-label"
        >
          Description
        </label>

        <textarea
          id="category-description"
          value={description}
          onChange={(event) => {
            setDescription(
              event.target.value
            );

            if (errors.description) {
              setErrors((current) => ({
                ...current,
                description: ""
              }));
            }
          }}
          placeholder="Enter category description"
          rows={5}
          disabled={loading}
          className="category-form-textarea"
        />

        <div className="category-description-footer">
          {errors.description ? (
            <p className="category-form-field-error">
              {errors.description}
            </p>
          ) : (
            <span />
          )}

          <span className="category-character-count">
            {description.length}/5000
          </span>
        </div>
      </div>

      <div className="category-form-field">
        <label
          htmlFor="category-display-order"
          className="category-form-label"
        >
          Display order
        </label>

        <input
          id="category-display-order"
          type="number"
          min="0"
          step="1"
          value={displayOrder}
          onChange={(event) => {
            setDisplayOrder(
              event.target.value
            );

            if (errors.displayOrder) {
              setErrors((current) => ({
                ...current,
                displayOrder: ""
              }));
            }
          }}
          disabled={loading}
          className="category-form-input"
        />

        {errors.displayOrder && (
          <p className="category-form-field-error">
            {errors.displayOrder}
          </p>
        )}
      </div>

      {mode === "edit" && (
        <label className="category-active-checkbox">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) =>
              setIsActive(
                event.target.checked
              )
            }
            disabled={loading}
          />

          <span>
            Category is active
          </span>
        </label>
      )}

      <div className="category-form-actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </Button>

        <Button
          type="submit"
          loading={loading}
          disabled={loading}
        >
          {mode === "edit"
            ? "Update category"
            : "Create category"}
        </Button>
      </div>
    </form>
  );
};

export default CategoryForm;